<template>
  <div class="filter-list">
    <div
      ref="filterToggleButton"
      class="filter-list__button-container"
    >
      <button
        type="button"
        class="button button--center button--cta filter-list__toggle"
        @click="openPanel"
      >
        <IconFilter class="icon icon--filter" />
        <span>{{ $t('Filter & Sort') }}
          <small
            v-if="extendedActiveFiltersCount"
            class="filter-list__count"
          >
            {{ extendedActiveFiltersCount }}
          </small>
        </span>
      </button>
    </div>

    <div
      :class="{ 'backdrop--active': open }"
      class="backdrop"
      @click="resetAllAndClose"
    />

    <search
      :class="{ 'filter-list__panel--open': open }"
      class="filter-list__panel"
    >
      <div class="filter-list__title">
        <span
          class="filter-list__label heading heading--5"
        >
          {{ $t('Filter & Sort') }}
        </span>
        <div
          class="button--link filter-list__close"
          @click="resetAllAndClose"
        >
          <span>{{ $t('Close') }}</span>
          <IconClose
            class="notification__close"
            @click="close()"
          />
        </div>
      </div>

      <!-- <UiScrollable :enabled="enableScrolling.value" class="filter-list__others filter-list__scrollable" axis="y"> -->
      <UiScrollable
        :enabled="true"
        class="filter-list__others filter-list__scrollable"
        axis="y"
      >
        <div class="paginator">
          <LazyPaginatorSortLinks
            class="sort paginator__sort-links"
            :sort="sort"
            :sort-options="sortOptions"
            @update-sort="updateSort"
          />
        </div>

        <FilterActive
          v-if="extendedActiveFiltersCount"
          class="plp__filter-active"
          :active-filters="extendedActiveFilters"
          @filters-reset-all="resetAndApplyAll"
          @filter-reset-range-min="resetMin"
          @filter-reset-range-max="resetMax"
          @option-toggle="toggleActive"
        />

        <FilterItem
          v-for="filter in extendedFilters"
          :key="filter.key"
          :filter="filter"
          :active-filter-keys="activeFilterKeys"
        />
      </UiScrollable>

      <div
        v-if="!applyOptionsDirectly"
        class="filter-list__buttons"
      >
        <button
          type="button"
          class="filter-list__apply-all button button--cta button--full"
          @click="applyAllAndClose"
        >
          <span>{{ $t('Show') }} {{ $t('items', { quantity: expectedTotal }, expectedTotal) }}</span>
        </button>
      </div>
    </search>

    <ClientOnly>
      <Teleport
        defer
        to="#teleports"
      >
        <Transition name="slide">
          <div
            v-if="isPastfilterToggleButton"
            class="plp__sticky-filter-toggle"
          >
            <div class="plp__sticky-filter-toggle--container">
              <div class="plp__sticky-filter-toggle--button">
                <button
                  type="button"
                  class="button button--center button--cta filter-list__toggle"
                  @click="openPanel"
                >
                  <IconFilter class="icon icon--filter" />
                  <span>{{ $t('Filter & Sort') }}
                    <small
                      v-if="extendedActiveFiltersCount"
                      class="filter-list__count"
                    >
                      {{ extendedActiveFiltersCount }}
                    </small>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
const { vuBreakpoints } = useApp()
const meta = inject('meta', ref())
const { sort, sortOptions, updateSort } = await useCatalogPaginator(meta)

const enableScrolling = ref(false)
const tooltips = inject('tooltips', ref())

const filterToggleButton = ref(null)
const filterToggleButtonVisible = ref(true)

useIntersectionObserver(
  filterToggleButton,
  ([entry]) => {
    filterToggleButtonVisible.value = entry?.isIntersecting || false
  },
  { rootMargin: '-10px 0px 0px 0px' },
)

const isPastfilterToggleButton = computed(() => !filterToggleButtonVisible.value && (filterToggleButton.value as HTMLElement | null)?.getBoundingClientRect().top < 0)

const {
  filterRangeResetMin,
  filterRangeResetMax,
  filterApply,
  optionToggle,
  activeFilters,
  applyOptionsDirectly,
  expectedTotal,
  filters,
  filtersApplyAll,
  filtersRemoveAll,
} = useCatalogFilters(meta)

const {
  open,
  openPanel,
  closePanel,
} = useCatalogFiltersModal()

const extendedFilters = computed(() => {
  return filters.value
    ?.filter(filter => !['cat', 'categories', 'is_outlet'].includes(filter.key))
    .map((filter) => {
      return {
        ...filter,
        tooltip: tooltips.value?.find(tip => tip.MagentoAttributeCode === filter.attribute)?.Richtext,
      }
    })
})

const extendedActiveFilters = computed(() =>
  activeFilters.value,
)

const extendedActiveFiltersCount = computed(() =>
  extendedActiveFilters.value.length,
)

const activeFilterKeys = computed(() =>
  activeFilters.value.map(({ key }) => key),
)

onMounted(() => {
  enableScrolling.value = vuBreakpoints.smallerOrEqual('phablet').value

  watchEffect(() => {
    applyOptionsDirectly.value = vuBreakpoints.greaterOrEqual('desktop').value
  })
})

function applyAllAndClose() {
  filtersApplyAll()
  closePanel()
}

function resetAllAndClose() {
  closePanel()
  filtersRemoveAll()
}

function toggleActive(filterKey: string, optionKey: string) {
  optionToggle(filterKey, optionKey)
  filterApply(filterKey)
}

function resetMin(filterKey: string) {
  filterRangeResetMin(filterKey)
  filterApply(filterKey)
}

function resetMax(filterKey: string) {
  filterRangeResetMax(filterKey)
  filterApply(filterKey)
}

function resetAndApplyAll() {
  filtersRemoveAll()
  filtersApplyAll()
}
</script>

<style>
@import url("~layers/app/components/Filter/List.css");
</style>
